import { v, assertPartialSchema, createAction, useSelector } from "../../../lib";

import { DexcomIntegration } from "../../../model";
import { ProjectCollaboratorType } from "../../../shared/db";
import { IProject, IWorkspace } from "../../../shared/db";

// 用enum來定義actionType的型別避免因typo造成錯誤
export enum ActionType {
  WORKSPACE_SET = "WORKSPACE_SET",
  WORKSPACE_NAME_SET = "WORKSPACE_NAME_SET",
  WORKSPACE_LIST_SET = "WORKSPACE_LIST_SET",
  WORKSPACE_DELETE = "WORKSPACE_DELETE",
  CURRENT_WORKSPACE_SET = "CURRENT_WORKSPACE_SET",
  WORKSPACE_ROLE_LIST_SET = "WORKSPACE_ROLE_LIST_SET",
  WORKSPACE_DEXCOM_INTEGRATION_LIST_SET = "WORKSPACE_DEXCOM_INTEGRATION_LIST_SET",
  WORKSPACE_DEXCOM_INTEGRATION_SET = "WORKSPACE_DEXCOM_INTEGRATION_SET",
  WORKSPACE_DEXCOM_INTEGRATION_DELETE = "WORKSPACE_DEXCOM_INTEGRATION_DELETE",
  GLOBAL_WORKSPACE_LIST_SET = "GLOBAL_WORKSPACE_LIST_SET",
}

export const doWORKSPACE_SET = createAction(ActionType.WORKSPACE_SET);
export const doWORKSPACE_NAME_SET = createAction(ActionType.WORKSPACE_NAME_SET);
export const doWORKSPACE_LIST_SET = createAction(ActionType.WORKSPACE_LIST_SET);
export const doWORKSPACE_DELETE = createAction(ActionType.WORKSPACE_DELETE);
export const doCURRENT_WORKSPACE_SET = createAction(ActionType.CURRENT_WORKSPACE_SET);
export const doWORKSPACE_ROLE_LIST_SET = createAction(ActionType.WORKSPACE_ROLE_LIST_SET);
export const doWORKSPACE_DEXCOM_INTEGRATION_LIST_SET = createAction(
  ActionType.WORKSPACE_DEXCOM_INTEGRATION_LIST_SET,
);
export const doWORKSPACE_DEXCOM_INTEGRATION_SET = createAction(
  ActionType.WORKSPACE_DEXCOM_INTEGRATION_SET,
);
export const doWORKSPACE_DEXCOM_INTEGRATION_DELETE = createAction(
  ActionType.WORKSPACE_DEXCOM_INTEGRATION_DELETE,
);
export const doGLOBAL_WORKSPACE_LIST_SET = createAction(ActionType.GLOBAL_WORKSPACE_LIST_SET);

export interface GlobalWorkspace extends IWorkspace {
  projectList: IProject[];
}
interface WorkspacesState {
  [key: string]: any;
  currentWorkspaceId: string;
  roleInWorkspaces: Record<string, any>;
  roleInProjects: Record<string, any>;
  globalWorkspacesList: GlobalWorkspace[];
}

// 這邊需要定義RootState這樣selector才能正常運作
interface RootState {
  workspaces: WorkspacesState;
}

export const useSelectWorkspaces = () => {
  return useSelector((state: RootState) => state.workspaces);
};

export const workspacesActionCreators = {
  doWORKSPACE_SET,
  doWORKSPACE_LIST_SET,
  doWORKSPACE_DELETE,
  doCURRENT_WORKSPACE_SET,
  doWORKSPACE_ROLE_LIST_SET,
  doWORKSPACE_NAME_SET,
};

export const workspacesDefaultState: WorkspacesState = {
  roleInWorkspaces: {},
  roleInProjects: {},
  currentWorkspaceId: "",
  globalWorkspacesList: [],
};

// 用union的方式來定義action並搭配reducer中的case語句可以防止未知type或錯誤的參數被傳入
type Action =
  | {
      type: ActionType.WORKSPACE_SET;
      payload: {
        id: string;
        name: string;
        identityId: string;
        participantLimit: number;
        projectCollaboratorList: any[];
      };
    }
  | {
      type: ActionType.WORKSPACE_NAME_SET;
      payload: {
        id: string;
        name: string;
      };
    }
  | {
      type: ActionType.WORKSPACE_DELETE;
      payload: {
        id: string;
      };
    }
  | {
      type: ActionType.WORKSPACE_LIST_SET;
      payload: {
        id: string;
        name: string;
        identityId: string;
        participantLimit: number;
      }[];
    }
  | {
      type: ActionType.CURRENT_WORKSPACE_SET;
      payload: {
        id: string;
      };
    }
  | {
      type: ActionType.WORKSPACE_ROLE_LIST_SET;
      payload: {
        workspaceId: string;
        type: string;
      }[];
    }
  | {
      type: ActionType.WORKSPACE_DEXCOM_INTEGRATION_LIST_SET;
      payload: {
        workspaceId: string;
        dexcomIntegrationList: {
          dexcomApp?: any;
          id: string;
          name: string;
          description: string;
          investigator: string;
          organization: string;
          countries: string;
          contactDescription: string;
        }[];
      };
    }
  | {
      type: ActionType.WORKSPACE_DEXCOM_INTEGRATION_SET;
      payload: {
        workspaceId: string;
        dexcomApp?: any;
        id: string;
        name: string;
        description: string;
        investigator: string;
        organization: string;
        countries: string;
        contactDescription: string;
      };
    }
  | {
      type: ActionType.WORKSPACE_DEXCOM_INTEGRATION_DELETE;
      payload: {
        workspaceId: string;
        dexcomIntegrationId: string;
      };
    }
  | {
      type: ActionType.GLOBAL_WORKSPACE_LIST_SET;
      payload: GlobalWorkspace[];
    };

/* Handle state changing of default workspace */
export const workspacesReducer = (
  state = workspacesDefaultState,
  { type, payload }: Action,
): WorkspacesState => {
  if (!Object.values(ActionType).includes(type)) {
    return state;
  }

  const newState: WorkspacesState = { ...state };
  let targetIndex: number;

  switch (type) {
    case ActionType.WORKSPACE_SET:
      assertPartialSchema({
        payload,
        schema: v.object({
          id: v.string().uuid().exist(),
          name: v.string().exist(),
          identityId: v.string().uuid().exist(),
          participantLimit: v.number().integer().exist(),
          projectCollaboratorList: v.array().allow(null).optional(),
        }),
      });

      newState[payload.id] = {
        ...newState[payload.id],
        ...payload,
      };

      return { ...newState };

    case ActionType.WORKSPACE_NAME_SET:
      assertPartialSchema({
        payload,
        schema: v.object({
          id: v.string().uuid().exist(),
          name: v.string().exist(),
        }),
      });

      newState[payload.id] = {
        ...(newState[payload.id] ?? {}),
        name: payload.name,
      };

      return { ...newState };

    case ActionType.WORKSPACE_DELETE:
      assertPartialSchema({
        payload,
        schema: v.object({
          id: v.string().uuid().exist(),
        }),
      });

      delete newState[payload.id];

      return { ...newState };

    case ActionType.WORKSPACE_LIST_SET:
      assertPartialSchema({
        payload,
        schema: v.array().items(
          v.object({
            id: v.string().uuid().exist(),
            name: v.string().exist(),
            identityId: v.string().uuid().exist(),
            participantLimit: v.number().integer().exist(),
          }),
        ),
      });

      payload.map((workspace: { id: string }) => {
        newState[workspace.id] = {
          ...newState[workspace.id],
          ...workspace,
        };
      });

      return { ...newState };

    case ActionType.CURRENT_WORKSPACE_SET:
      assertPartialSchema({
        payload,
        schema: v.object({
          id: v.string().uuid().allow("sharedWithMe", "favorites").exist(),
        }),
      });

      return { ...newState, currentWorkspaceId: payload.id };

    case ActionType.WORKSPACE_ROLE_LIST_SET: {
      assertPartialSchema({
        payload,
        schema: v.array().items(
          v.object({
            workspaceId: v.string().uuid().exist(),
            type: v.string().exist(),
          }),
        ),
      });
      const roleInWorkspaces: Record<string, any> = { ...newState.roleInWorkspaces };
      const roleInProjects: Record<string, any> = { ...newState.roleInProjects };
      const collaboratorTypeInWorkspaceList = payload as {
        workspaceId: string;
        type: string;
        projectCollaboratorList: { projectId: string; type: ProjectCollaboratorType }[];
      }[];

      collaboratorTypeInWorkspaceList.forEach(({ workspaceId, type }) => {
        roleInWorkspaces[workspaceId] = type;
      });
      collaboratorTypeInWorkspaceList
        .flatMap(({ projectCollaboratorList }) => projectCollaboratorList)
        .forEach(({ projectId, type }) => {
          roleInProjects[projectId] = type;
        });
      return { ...newState, roleInWorkspaces, roleInProjects };
    }

    case ActionType.WORKSPACE_DEXCOM_INTEGRATION_LIST_SET:
      assertPartialSchema({
        payload,
        schema: v.object({
          workspaceId: v.string().uuid().exist(),
          dexcomIntegrationList: v
            .array()
            .exist()
            .items(
              v.object({
                id: v.string().uuid().exist(),
                dexcomApp: v.object().allow(null).optional(),
                name: v.string().exist(),
                description: v.string().exist(),
                investigator: v.string().exist(),
                organization: v.string().exist(),
                countries: v.string().exist(),
                contactDescription: v.string().exist(),
              }),
            ),
        }),
      });

      newState[payload.workspaceId] = {
        ...newState[payload.workspaceId],
        dexcomIntegrationList: [...payload.dexcomIntegrationList],
      };
      return { ...newState };
    case ActionType.WORKSPACE_DEXCOM_INTEGRATION_SET: {
      assertPartialSchema({
        payload,
        schema: v.object({
          id: v.string().uuid().exist(),
          workspaceId: v.string().uuid().exist(),
          dexcomApp: v.object().allow(null).optional(),
          name: v.string().exist(),
          description: v.string().exist(),
          investigator: v.string().exist(),
          organization: v.string().exist(),
          countries: v.string().exist(),
          contactDescription: v.string().exist(),
        }),
      });

      const workspace = newState[payload.workspaceId] ?? {};
      const dexcomIntegrationList = workspace.dexcomIntegrationList ?? [];

      targetIndex = dexcomIntegrationList.findIndex(
        (item: DexcomIntegration) => item.id === payload.id,
      );

      newState[payload.workspaceId] = {
        ...workspace,
        dexcomIntegrationList:
          targetIndex !== -1
            ? dexcomIntegrationList.map((item: DexcomIntegration, index: number) =>
                index === targetIndex ? payload : item,
              )
            : [...dexcomIntegrationList, payload],
      };

      return { ...newState };
    }

    case ActionType.WORKSPACE_DEXCOM_INTEGRATION_DELETE: {
      assertPartialSchema({
        payload,
        schema: v.object({
          workspaceId: v.string().uuid().exist(),
          dexcomIntegrationId: v.string().exist(),
        }),
      });

      const targetWorkspace = newState[payload.workspaceId];
      const targetDexcomIntegrationList = targetWorkspace?.dexcomIntegrationList ?? [];
      targetIndex = targetDexcomIntegrationList.findIndex(
        (item: DexcomIntegration) => item.id === payload.dexcomIntegrationId,
      );

      if (targetIndex !== -1) {
        newState[payload.workspaceId] = {
          ...targetWorkspace,
          dexcomIntegrationList: targetDexcomIntegrationList.filter(
            (item: DexcomIntegration) => item.id !== payload.dexcomIntegrationId,
          ),
        };
      }

      return { ...newState };
    }

    case ActionType.GLOBAL_WORKSPACE_LIST_SET:
      assertPartialSchema({
        payload,
        schema: v.array().items(
          v.object({
            id: v.string().uuid().exist(),
            name: v.string().exist(),
            identityId: v.string().uuid().exist(),
            participantLimit: v.number().integer().exist(),
          }),
        ),
      });

      return { ...state, globalWorkspacesList: payload };
    default:
      return state;
  }
};
